import { API_BASE_URL } from '../../config/apiConfig';
import { dispatchTicketingDataChanged } from './ticketingStorage';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiJson(path, options = {}) {
  const res = await fetch(`${API_BASE_URL}/ticketing${path}`, {
    ...options,
    headers: { ...authHeaders(), ...options.headers },
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text || res.statusText;
    try {
      const j = JSON.parse(text);
      if (Array.isArray(j.message)) msg = j.message.join(', ');
      else if (typeof j.message === 'string') msg = j.message;
      else if (j.error) msg = j.error;
    } catch {
      /* use text */
    }
    throw new Error(msg);
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function mapCategoryRow(c) {
  if (!c) return c;
  return {
    id: c.id,
    departmentId: c.ticketingDepartmentId,
    name: c.name,
    categoryGroup: c.categoryGroup,
    description: c.description,
    formKey: c.formKey,
    isActive: c.isActive,
    record_order: c.record_order,
    createdAt: c.createdAt,
  };
}

export function mapDepartmentRow(d) {
  if (!d) return d;
  return {
    id: d.id,
    name: d.name,
    description: d.description,
    isActive: d.isActive,
    record_order: d.record_order,
    userDepartmentId: d.userDepartmentId ?? null,
    userDepartment: d.userDepartment
      ? {
          id: d.userDepartment.id,
          name: d.userDepartment.name,
          isActive: d.userDepartment.isActive,
          record_order: d.userDepartment.record_order,
        }
      : null,
    createdAt: d.createdAt,
  };
}

// --- Departments ---

export async function fetchTicketingDepartments() {
  const rows = await apiJson('/departments');
  return (rows || []).map(mapDepartmentRow);
}

export async function fetchTicketingDepartment(id) {
  const d = await apiJson(`/departments/${id}`);
  const base = mapDepartmentRow(d);
  const rawCats = d.categories || [];
  return {
    ...base,
    categories: rawCats.map(mapCategoryRow),
  };
}

export async function createTicketingDepartment(body) {
  const row = await apiJson('/departments', {
    method: 'POST',
    body: JSON.stringify({
      name: body.name,
      description: body.description,
      isActive: body.isActive !== false,
      record_order: body.record_order,
      userDepartmentId: body.userDepartmentId,
    }),
  });
  dispatchTicketingDataChanged();
  return mapDepartmentRow(row);
}

export async function updateTicketingDepartment(id, body) {
  const row = await apiJson(`/departments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      name: body.name,
      description: body.description,
      isActive: body.isActive,
      record_order: body.record_order,
      userDepartmentId: body.userDepartmentId,
    }),
  });
  dispatchTicketingDataChanged();
  return mapDepartmentRow(row);
}

export async function deleteTicketingDepartment(id) {
  await apiJson(`/departments/${id}`, { method: 'DELETE' });
  dispatchTicketingDataChanged();
}

// --- Categories ---

export async function fetchTicketingCategories(departmentId) {
  const q =
    departmentId != null && departmentId !== ''
      ? `?departmentId=${encodeURIComponent(departmentId)}`
      : '';
  const rows = await apiJson(`/categories${q}`);
  return (rows || []).map(mapCategoryRow);
}

export async function createTicketingCategory(body) {
  const row = await apiJson('/categories', {
    method: 'POST',
    body: JSON.stringify({
      ticketingDepartmentId: body.departmentId,
      name: body.name,
      categoryGroup: body.categoryGroup,
      formKey: body.formKey,
      description: body.description,
      isActive: body.isActive !== false,
      record_order: body.record_order,
    }),
  });
  dispatchTicketingDataChanged();
  return mapCategoryRow(row);
}

export async function updateTicketingCategory(id, body) {
  const payload = {};
  if (body.name !== undefined) payload.name = body.name;
  if (body.categoryGroup !== undefined) payload.categoryGroup = body.categoryGroup;
  if (body.formKey !== undefined) payload.formKey = body.formKey;
  if (body.description !== undefined) payload.description = body.description;
  if (body.isActive !== undefined) payload.isActive = body.isActive;
  if (body.record_order !== undefined) payload.record_order = body.record_order;
  if (body.departmentId !== undefined) {
    payload.ticketingDepartmentId = body.departmentId;
  }
  const row = await apiJson(`/categories/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  dispatchTicketingDataChanged();
  return mapCategoryRow(row);
}

export async function deleteTicketingCategory(id) {
  await apiJson(`/categories/${id}`, { method: 'DELETE' });
  dispatchTicketingDataChanged();
}

// --- Requests ---

export async function createTicketingRequest({
  ticketingDepartmentId,
  ticketingCategoryId,
  formKey,
  ticketDate,
  subject,
  payload,
}) {
  return apiJson('/requests', {
    method: 'POST',
    body: JSON.stringify({
      ticketingDepartmentId,
      ticketingCategoryId,
      formKey,
      ticketDate,
      subject,
      payload,
    }),
  });
}

export async function fetchTicketingRequests(query = {}) {
  const params = new URLSearchParams();
  if (query.departmentId != null && query.departmentId !== '') {
    params.set('departmentId', String(query.departmentId));
  }
  if (query.categoryId != null && query.categoryId !== '') {
    params.set('categoryId', String(query.categoryId));
  }
  if (query.status) params.set('status', query.status);
  if (query.search != null && String(query.search).trim() !== '') {
    params.set('search', String(query.search).trim());
  }
  const q = params.toString();
  return apiJson(q ? `/requests?${q}` : '/requests');
}

/** JWT — current user’s tickets; use departmentId to scope to one department. */
export async function fetchMyTicketingRequests(query = {}) {
  const params = new URLSearchParams();
  if (query.departmentId != null && query.departmentId !== '') {
    params.set('departmentId', String(query.departmentId));
  }
  if (query.status) params.set('status', query.status);
  if (query.search != null && String(query.search).trim() !== '') {
    params.set('search', String(query.search).trim());
  }
  const q = params.toString();
  return apiJson(q ? `/requests/mine?${q}` : '/requests/mine');
}

export async function fetchTicketingRequest(id) {
  return apiJson(`/requests/${encodeURIComponent(id)}`);
}

export async function updateTicketingRequest(id, body) {
  const row = await apiJson(`/requests/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  dispatchTicketingDataChanged();
  return row;
}

export async function deleteTicketingRequest(id) {
  await apiJson(`/requests/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  dispatchTicketingDataChanged();
}

/** Requires JWT — comments between requester and IT. */
export async function fetchTicketingRequestMessages(requestId) {
  return apiJson(`/requests/${encodeURIComponent(requestId)}/messages`);
}

/** Requires JWT */
export async function postTicketingRequestMessage(requestId, text) {
  const created = await apiJson(`/requests/${encodeURIComponent(requestId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body: text }),
  });
  dispatchTicketingDataChanged();
  return created;
}

/** JWT — active users for assignee dropdown (same as /users). */
export async function fetchTicketingAssignableUsers() {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${API_BASE_URL}/users`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text || res.statusText;
    try {
      const j = JSON.parse(text);
      if (typeof j.message === 'string') msg = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (!text) return [];
  try {
    const j = JSON.parse(text);
    if (!Array.isArray(j)) return [];
    return j.filter((user) => user?.isIT === true && user?.userIsActive !== false);
  } catch {
    return [];
  }
}

/** JWT — users filtered to one department (for ticket filters). */
export async function fetchTicketingDepartmentUsers({
  linkedUserDepartmentId,
} = {}) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${API_BASE_URL}/users`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text || res.statusText;
    try {
      const j = JSON.parse(text);
      if (typeof j.message === 'string') msg = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (!text) return [];
  try {
    const j = JSON.parse(text);
    if (!Array.isArray(j)) return [];
    if (linkedUserDepartmentId == null || linkedUserDepartmentId === '') return [];
    return j.filter((u) => {
      if (!u || u.userIsActive === false) return false;
      return String(u.departmentId ?? '') === String(linkedUserDepartmentId);
    });
  } catch {
    return [];
  }
}

/** JWT — user department list for linking ticketing departments. */
export async function fetchUserDepartments() {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${API_BASE_URL}/user-department`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text || res.statusText;
    try {
      const j = JSON.parse(text);
      if (typeof j.message === 'string') msg = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (!text) return [];
  try {
    const j = JSON.parse(text);
    if (!Array.isArray(j)) return [];
    return j;
  } catch {
    return [];
  }
}

/** JWT — multipart upload; optional messageId ties file to a comment message. */
export async function uploadTicketingRequestAttachment(requestId, file, messageId) {
  const token = localStorage.getItem('authToken');
  const fd = new FormData();
  fd.append('file', file);
  if (messageId != null && messageId !== '') {
    fd.append('messageId', String(messageId));
  }
  const res = await fetch(
    `${API_BASE_URL}/ticketing/requests/${encodeURIComponent(requestId)}/attachments`,
    {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: fd,
    },
  );
  const text = await res.text();
  if (!res.ok) {
    let msg = text || res.statusText;
    try {
      const j = JSON.parse(text);
      if (typeof j.message === 'string') msg = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  dispatchTicketingDataChanged();
  return text ? JSON.parse(text) : null;
}

/** JWT — returns object URL; caller should revokeObjectURL when done. */
export async function downloadTicketingAttachmentObjectUrl(requestId, attachmentId) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(
    `${API_BASE_URL}/ticketing/requests/${encodeURIComponent(requestId)}/attachments/${encodeURIComponent(attachmentId)}/file`,
    {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || res.statusText);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
